import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Clock } from 'lucide-react';
import axios from 'axios';
import { SEO, SchemaOrg } from '../components/SEO';
import { CommentsSection } from '../components/CommentsSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const renderMarkdown = (md = '') =>
  md
    .replace(/## /g, '<h2>')
    .replace(/### /g, '<h3>')
    .replace(/#### /g, '<h4>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/- (.*)/g, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)+/g, '<ul>$&</ul>');

export const GuidePage = () => {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [allGuides, setAllGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [guideRes, allGuidesRes] = await Promise.all([
          axios.get(`${API}/guides/${slug}`),
          axios.get(`${API}/guides`),
        ]);
        setGuide(guideRes.data);
        setAllGuides(allGuidesRes.data);
        setError(null);
      } catch (err) {
        setError('Guide not found');
        console.error('Error fetching guide:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="w-6 h-6 border border-[#111] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !guide) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAF9F6]">
        <p className="overline overline-gold mb-4">404</p>
        <h1 className="heading-editorial text-4xl mb-8">Guide Not Found</h1>
        <Link to="/" className="btn-editorial">Return Home</Link>
      </div>
    );
  }

  const currentIndex = allGuides.findIndex((g) => g.slug === slug);
  const prevGuide = currentIndex > 0 ? allGuides[currentIndex - 1] : null;
  const nextGuide = currentIndex < allGuides.length - 1 ? allGuides[currentIndex + 1] : null;
  const otherGuides = allGuides.filter((g) => g.slug !== slug);

  return (
    <div data-testid={`guide-page-${slug}`} className="bg-[#FAF9F6]">
      <SEO
        title={guide.meta_title || guide.title}
        description={guide.meta_description || guide.excerpt}
        image={guide.image_url}
        url={`/guides/${guide.slug}`}
        type="article"
      />
      <SchemaOrg
        type="article"
        data={{
          title: guide.title,
          description: guide.excerpt,
          image: guide.image_url,
          url: `/guides/${guide.slug}`,
        }}
      />
      <SchemaOrg
        type="breadcrumb"
        data={{
          items: [
            { name: 'Home', url: '/' },
            { name: 'Guides', url: '/' },
            { name: guide.title, url: `/guides/${guide.slug}` },
          ],
        }}
      />

      <article>
        {/* Editorial Header */}
        <header className="border-b border-[#E5E0D8]">
          <div className="container-editorial py-12 lg:py-20">
            <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-[#888] mb-12">
              <Link to="/" className="hover:text-[#111] transition-colors">Home</Link>
              <span>/</span>
              <span className="text-[#111]">Guides</span>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
              <div className="lg:col-span-9">
                <p className="overline overline-gold mb-6">The Definitive Guide</p>
                <h1 className="font-heading font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#111] mb-8">
                  {guide.title}
                </h1>
                {guide.excerpt && (
                  <p className="text-xl lg:text-2xl text-[#555] leading-relaxed font-light max-w-3xl">
                    {guide.excerpt}
                  </p>
                )}
              </div>
              <div className="lg:col-span-3 lg:border-l border-[#E5E0D8] lg:pl-8">
                <p className="overline mb-3">Reading Time</p>
                <p className="font-heading text-2xl text-[#111] mb-6 flex items-center gap-2">
                  <Clock className="w-4 h-4" strokeWidth={1.5} />
                  8 minutes
                </p>
                <p className="overline mb-3">Reviewed By</p>
                <p className="text-sm text-[#555]">GIA-certified gemologists</p>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Image */}
        <figure className="container-editorial pt-12 lg:pt-16">
          <div className="aspect-[16/9] overflow-hidden img-zoom">
            <img src={guide.image_url} alt={guide.title} className="w-full h-full object-cover" />
          </div>
        </figure>

        {/* Body */}
        <section className="container-editorial py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-8 lg:col-start-2">
              <div
                className="prose-editorial"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(guide.content) }}
              />

              {/* CTA */}
              <div className="mt-16 pt-16 border-t border-[#E5E0D8]">
                <div className="bg-[#111] text-white p-10 lg:p-14">
                  <p className="overline text-[#C69C6D] mb-4">Continue The Journey</p>
                  <h3 className="font-heading text-3xl lg:text-4xl font-light leading-tight mb-4">
                    Ready to Start <span className="italic">Shopping?</span>
                  </h3>
                  <p className="text-white/60 leading-relaxed mb-8 max-w-md">
                    Browse our vetted partners for authenticated pre-owned luxury jewelry from
                    the world's most prestigious houses.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Link to="/partners" className="btn-editorial border-white text-white hover:bg-white hover:text-[#111]">
                      Trusted Partners
                    </Link>
                    <Link to="/compare" className="btn-editorial border-[#C69C6D] text-[#C69C6D] hover:bg-[#C69C6D] hover:text-white">
                      Compare Platforms
                    </Link>
                  </div>
                </div>
              </div>

              {/* Comments */}
              <CommentsSection postSlug={guide.slug} postType="guide" />
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-3 lg:col-start-10">
              <div className="sticky top-32 space-y-12">
                {otherGuides.length > 0 && (
                  <div>
                    <p className="overline overline-gold mb-6">More Guides</p>
                    <ul className="space-y-6">
                      {otherGuides.map((g) => (
                        <li key={g.id} className="pb-6 border-b border-[#E5E0D8] last:border-b-0">
                          <Link to={`/guides/${g.slug}`} className="block group">
                            <p className="font-heading text-lg leading-snug text-[#111] group-hover:text-[#C69C6D] transition-colors mb-2">
                              {g.title}
                            </p>
                            <p className="text-xs text-[#888] line-clamp-2 leading-relaxed">{g.excerpt}</p>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="border-t border-[#E5E0D8] pt-12">
                  <p className="overline overline-gold mb-6">Quick Reads</p>
                  <ul className="space-y-3">
                    {[
                      { to: '/compare', label: 'Compare Platforms' },
                      { to: '/designers/cartier', label: 'Browse Cartier' },
                      { to: '/partners', label: 'Trusted Partners' },
                    ].map((q) => (
                      <li key={q.to}>
                        <Link
                          to={q.to}
                          className="flex items-center justify-between py-2 border-b border-[#E5E0D8] hover:border-[#C69C6D] transition-colors group"
                        >
                          <span className="text-sm text-[#555] group-hover:text-[#111] transition-colors">{q.label}</span>
                          <ArrowRight className="w-3 h-3 text-[#888] group-hover:text-[#C69C6D] transition-colors" strokeWidth={1.5} />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </section>

        {/* Prev/Next Navigation */}
        <section className="border-t border-[#E5E0D8] bg-[#111] text-white">
          <div className="container-editorial py-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {prevGuide ? (
                <Link
                  to={`/guides/${prevGuide.slug}`}
                  className="group flex items-start gap-4 md:border-r border-white/10 md:pr-8"
                >
                  <ArrowLeft className="w-4 h-4 mt-1.5 text-[#C69C6D] group-hover:-translate-x-1 transition-transform" strokeWidth={1.5} />
                  <div>
                    <p className="overline text-[#C69C6D] mb-2">Previous Guide</p>
                    <p className="font-heading text-lg lg:text-xl text-white group-hover:text-[#C69C6D] transition-colors line-clamp-2">
                      {prevGuide.title}
                    </p>
                  </div>
                </Link>
              ) : (
                <div />
              )}

              {nextGuide ? (
                <Link
                  to={`/guides/${nextGuide.slug}`}
                  className="group flex items-start gap-4 md:justify-end md:text-right md:pl-8"
                >
                  <div className="md:order-2">
                    <p className="overline text-[#C69C6D] mb-2">Next Guide</p>
                    <p className="font-heading text-lg lg:text-xl text-white group-hover:text-[#C69C6D] transition-colors line-clamp-2">
                      {nextGuide.title}
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 mt-1.5 text-[#C69C6D] group-hover:translate-x-1 transition-transform md:order-3" strokeWidth={1.5} />
                </Link>
              ) : (
                <div />
              )}
            </div>
          </div>
        </section>
      </article>
    </div>
  );
};

export default GuidePage;
